import Nav from "@/components/Nav";
import { ShieldCheck, AlertTriangle, Flag, Users } from "lucide-react";

export default function Policy() {
  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <div className="chip mb-4">Trust & Safety</div>
        <h1 className="font-serif-display text-4xl sm:text-6xl text-[#F7F3EC] tracking-tight mb-4">Content Policy</h1>
        <p className="font-serif-body text-lg text-[#A8A095] leading-relaxed mb-10 italic">
          Anonscribe Archive is a marketplace for unfinished literary work sold anonymously. Anonymity does not mean anything goes.
          The rules below are non-negotiable.
        </p>

        <section className="gold-frame paper-scroll rounded-sm p-6 sm:p-8 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#8C3A34]" />
            <h2 className="font-serif-display text-2xl text-[#F7F3EC]">Absolutely prohibited</h2>
          </div>
          <ul className="space-y-2 font-serif-body text-[#F7F3EC] leading-relaxed">
            <li>— <strong>Any sexual or exploitative depiction of a minor (person under 18)</strong>, real or fictional. This is a permanent, no-appeal ban.</li>
            <li>— Sexual content produced without the depicted person's consent, or presenting non-consensual acts approvingly.</li>
            <li>— Content designed to incite real-world violence against an identifiable person or group.</li>
            <li>— Doxxing, threats, or personal information of anyone other than yourself.</li>
          </ul>
        </section>

        <section className="gold-frame paper-scroll rounded-sm p-6 sm:p-8 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-[#D9B472]" />
            <h2 className="font-serif-display text-2xl text-[#F7F3EC]">How we enforce it</h2>
          </div>
          <ul className="space-y-2 font-serif-body text-[#A8A095] leading-relaxed">
            <li><span className="text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest">01</span> · Age & policy attestation at registration; the IP and timestamp of your agreement are stored.</li>
            <li><span className="text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest">02</span> · Every submitted manuscript is scanned by an automated classifier (keyword pre-filter + AI content review) before it becomes visible in the archive.</li>
            <li><span className="text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest">03</span> · Every uploaded cover image is SHA-256 hashed and checked against a growing blocklist of known-prohibited media. Attempts to re-upload known material result in instant account suspension.</li>
            <li><span className="text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest">04</span> · Any reader can report a work. Three reports auto-hide it pending human review.</li>
            <li><span className="text-[#D9B472] font-mono-brass text-xs uppercase tracking-widest">05</span> · Admin can hard-delete violating content and permanently suspend the associated pen name and email.</li>
          </ul>
        </section>

        <section className="gold-frame paper-scroll rounded-sm p-6 sm:p-8 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Flag className="w-5 h-5 text-[#D9B472]" />
            <h2 className="font-serif-display text-2xl text-[#F7F3EC]">Reporting</h2>
          </div>
          <p className="font-serif-body text-[#A8A095] leading-relaxed">
            Every listing has a "Report" button. Reports are anonymous to the author. Please describe what you saw in one or two sentences.
            If you believe content depicts a real child in danger, contact your local law enforcement AND the National Center for Missing & Exploited Children (NCMEC) at
            {" "}<a href="https://report.cybertip.org" className="text-[#D9B472] underline">report.cybertip.org</a>{" "}
            (US) or your national equivalent. We cooperate with law enforcement requests.
          </p>
        </section>

        <section className="gold-frame paper-scroll rounded-sm p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-[#D9B472]" />
            <h2 className="font-serif-display text-2xl text-[#F7F3EC]">What's still welcome</h2>
          </div>
          <p className="font-serif-body text-[#A8A095] leading-relaxed">
            Difficult, dark, literary work is welcome. Fiction that grapples with trauma, abuse in a survivor's voice,
            historical atrocities, adult sexuality between adult characters, moral ambiguity — this is a literary marketplace, not
            a family newsletter. We only flag when the text itself sexualises minors or celebrates non-consensual acts.
            If you're not sure, err on the side of clarity: say your characters are 18+ in the excerpt.
          </p>
        </section>

        <p className="text-xs text-[#6E675E] font-mono-brass uppercase tracking-widest mt-10 text-center">Last updated · February 2026</p>
      </div>
    </div>
  );
}
